import DashboardNavigationBar from '@/components/dashboard/navigation-bar/dashboard-navigation-bar';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import LoadMoreButton from '@/components/load-more-button/load-more-button';
import MainContent from '@/components/main-layout/components/main-content/main-content';
import RiskImpactGraph from '@/components/risk-impact-graph/risk-impact-graph';
import RisksCard from '@/components/risks-card/risks-card';
import { ROUTE_DASHBOARD, ROUTE_SINGLE_CANCER_VIEW } from '@/constants/routes';
import { FeatureFlags, useFeatureFlags } from '@/services/feature-flag-provider';
import { getRoute } from '@/utils';
import { Box, Divider, Text } from '@cmpsr/components';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RiskSpecificCancerChart from '../common/risk-specific-cancer-chart/risk-specific-cancer-chart';
import RiskTrendCard from '../common/risk-trend-card/risk-trend-card';
import RisksSectionTitle from '../common/risks-section-title/risks-section-title';
import SeeMoreDetailsLink from '../common/see-more-details-link/see-more-details-link';
import EmptyState from './components/empty-state/empty-state';
import OverallRiskTrendChart from './components/overall-risk-trend-chart/overall-risk-trend-chart';
import useRiskTrendController from './risk-trend.controller';
import TrendHeaderRisks from './components/trend-header-risks/trend-header-risks';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';

export default function RiskTrendPage() {
  const {
    overallDataTrend,
    overallDataTrendForChart,
    lastUpdatedDateFormatted,
    sortedCancerTrendsByRiskLevel,
    hasMoreRiskChanges,
    showMoreRiskChanges,
    specificCancerChartsData,
    isEmptyState,
  } = useRiskTrendController();

  const { isEnabled } = useFeatureFlags();
  const isRiskTrendTabEnabled = isEnabled(FeatureFlags.IsRiskTrendTabEnabled);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isRiskTrendTabEnabled) {
      navigate(getRoute(ROUTE_DASHBOARD));
    }
  }, [isRiskTrendTabEnabled, navigate]);

  return (
    <>
      <DashboardNavigationBar activeItem="risk-trend" />
      <MainContent>
        {isEmptyState ? (
          <>
            <TrendHeaderRisks headlineText="Your overall lifetime cancer" subheadlineText="risk trend" />
            <Box
              pt={{ base: 'spacer-4', md: 'spacer-3', lg: 'none' }}
              pb={{ base: 'spacer-5', md: 'spacer-8', lg: 'spacer-12' }}
              px={{ base: 'spacer-5', md: 'spacer-8', lg: 'spacer-16', xl: 'spacer-24', xxl: 'spacer-32' }}
              maxWidth="1440px"
              marginX="auto"
            >
              <EmptyState />
            </Box>
          </>
        ) : (
          <>
            <TrendHeaderRisks headlineText="Your overall lifetime cancer" subheadlineText="risk trend">
              <Text variant="text-body-meta-regular" color="text-secondary" mt={{ base: 'spacer-5', lg: 'spacer-4' }}>
                Last Updated: {lastUpdatedDateFormatted}
              </Text>
              <OverallRiskTrendChart
                risks={overallDataTrendForChart.risks}
                riskChanges={overallDataTrendForChart.riskChanges}
              />
            </TrendHeaderRisks>
            <DashboardLayout>
              <Divider />
              <Box>
                <RisksSectionTitle headline="What has changed?" />
                <RisksCard mt={{ base: 'spacer-5', lg: 'spacer-8' }}>
                  <RisksCard.Content>
                    {overallDataTrend?.map((trend, index) => (
                      <RiskTrendCard
                        key={index}
                        trend={trend}
                        trendIndex={index}
                        totalTrendCount={overallDataTrend.length}
                        areAllTrendsLoaded={!hasMoreRiskChanges}
                      />
                    ))}
                  </RisksCard.Content>
                  {hasMoreRiskChanges && (
                    <RisksCard.Footer>
                      <LoadMoreButton onClick={showMoreRiskChanges} />
                    </RisksCard.Footer>
                  )}
                </RisksCard>
              </Box>
              <Divider />
              <Box>
                <RisksSectionTitle headline="Here are your risk trends for" subheadline="each type of cancer" />
                {sortedCancerTrendsByRiskLevel.map(([cancerType, { trends, risk, average }]) => (
                  <RisksCard mt="spacer-8" key={cancerType}>
                    <RisksCard.Header pb="0">
                      <RiskImpactGraph
                        cancerType={cancerType as TypeOfCancer}
                        impact={risk}
                        average={average}
                        isCancerPageLink
                      />
                    </RisksCard.Header>
                    <RisksCard.Content>
                      <RiskSpecificCancerChart
                        risks={specificCancerChartsData[cancerType as TypeOfCancer]?.risks}
                        riskChanges={specificCancerChartsData[cancerType as TypeOfCancer]?.riskChanges}
                      />
                    </RisksCard.Content>
                    <Divider />
                    <RisksCard.Content>
                      {trends.map((trend, index) => (
                        <RiskTrendCard key={index} trend={trend} />
                      ))}
                    </RisksCard.Content>
                    <RisksCard.Footer>
                      <SeeMoreDetailsLink href={getRoute(ROUTE_SINGLE_CANCER_VIEW, { cancerId: cancerType })} />
                    </RisksCard.Footer>
                  </RisksCard>
                ))}
              </Box>
            </DashboardLayout>
          </>
        )}
      </MainContent>
    </>
  );
}
